<template>
  <div>
    <BoxWithTitle
      collapsible
      openingIcon="mdi-chevron-down"
      closingIcon="mdi-chevron-up"
    >
      <template #header>
        <v-toolbar-title>
          {{ $t('SubNodeTypes.subNodeNetworkType') }}
        </v-toolbar-title>
      </template>
      <template #content>
        <v-skeleton-loader
          v-if="isFetching"
          class="m-auto"
          type="table"
          transition="scale-transition"
        />
        <TableRemote
          v-if="hasPermission('nodes.types.view')"
          :iconHeader="hasPermission('nodes.types.add') ? 'add' : null"
          :items="displayedNetworkTypes"
          isSearchable
          isCustomIcon
          :headers="tableHeaders"
          :itemActions="actions"
          :title="$t('SubNodeTypes.subNodeNetworkType')"
          :pagination="pagination"
          :centerColumns="['action']"
          :notSortableColumns="['action']"
          :sortableColumns="['name', 'type']"
          hideDefaultFooter
          @iconAction="onClickAction"
          @sendQueryParams="fetchQueryParams"
        >
          <template #withInfiniteScroll>
            <div v-intersect="endIntersect" />
          </template>
        </TableRemote>
        <NoPermissionAlert
          v-else
          :message="$t('permissions.error')"
        />
      </template>
    </BoxWithTitle>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import NetworkForm from './NetworkForm.vue';

export default {
    components: {
        BoxWithTitle,
        NoPermissionAlert,
        TableRemote,
    },
    data() {
        return {
            networkTypes: [],
            pagination: {},
            queryParams: { perpage: 25, page: 1 },
            isFetching: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
                'type',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedNetworkTypes() {
            return this.networkTypes.map(networkType => ({
                id: networkType.id,
                // eslint-disable-next-line vue/max-attributes-per-line
                name: !networkType.name[this.userLang] || !networkType.name[this.userLang].length
                    ? networkType.name[this.teamMainLang]
                    : networkType.name[this.userLang],
                type: networkType.key,

            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('nodes.types.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('nodes.types.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        await this.getNetworkTypes();
    },
    methods: {
        fetchQueryParams(queryParams) {
            if (!isEmpty(queryParams.order)) {
                const split = queryParams.order.split('_');
                const [filedName, sort] = split;
                // eslint-disable-next-line no-param-reassign
                if (filedName === 'type') queryParams.order = `key_${sort}`;
            }
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.getNetworkTypes();
        },
        async getNetworkTypes(nextPage = 1) {
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getNetworkTypes(this.queryParams);
                this.networkTypes = [...data.networkTypes];
                this.pagination = data.pagination;
                this.isFetching = false;
            }
            if (nextPage <= this.queryParams.page) {
                return;
            }

            this.isFetching = true;
            this.queryParams.page = nextPage;
            if (this.queryParams.page === this.pagination.currentPage) return;
            const { data } = await this.$rq.getNetworkTypes(this.queryParams);
            this.networkTypes = [...this.networkTypes, ...data.networkTypes];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('SubNodeTypes.addNetworkType'),
                component: Vue.component('networkForm', NetworkForm),
                payload: { usedKeys: this.networkTypeKeys },
                showLangs: true,
                isForm: true,
                onConfirm: requestPayload => this.addNetwork(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getNetworkType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.updateNetworkType'),
                    component: Vue.component('NetworkForm', NetworkForm),
                    showLangs: true,
                    isForm: false,
                    payload: { networkType: data, isEdition: true, usedKeys: this.networkTypeKeys },
                    onConfirm: requestPayload => this.updateNetwork(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('SubNodeTypes.removeNetworkType'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    isForm: false,
                    payload: { msg: this.$t('SubNodeTypes.removeNetworkType') },
                    onConfirm: () => this.removeNetwork(item.id),
                });
            }
        },

        async addNetwork(requestPayload) {
            await this.$rq.addNetworkType(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.added'),
            });
            await this.getNetworkTypes();
        },
        async updateNetwork(id, requestPayload) {
            await this.$rq.updateNetworkType(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.updated'),
            });
            await this.getNetworkTypes();
        },
        async removeNetwork(id) {
            await this.$rq.removeNetworkType(id);
            this.$toasted.global.toast_success({
                message: this.$t('SubNodeTypes.removed'),
            });
            await this.getNetworkTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getNetworkTypes(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
